import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent } from './ui/card';
import LocationPickerMap from './LocationPickerMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const RegisterEquipmentDialog = ({ isOpen, onOpenChange, currentUser }) => {
  const [step, setStep] = useState(2); // Start directly at form (skip category selection)
  const [selectedCategory, setSelectedCategory] = useState('piattaforme_aeree'); // Default to aerial platforms
  const [equipmentData, setEquipmentData] = useState({
    managerName: '',
    phoneNumber: '',
    rental_type: '',
    title: '',
    fuelType: '',
    maxHeight: '',
    nickname: '',
    setup: '',
    armType: '',
    movementMode: '',
    negativeWork: '',
    additionalFeatures: '',
    dailyRate: '',
    weeklyRate: '',
    monthlyRate: '',
    // New detailed location fields
    city: '',
    province: '',
    address: '',
    civicNumber: '',
    postalCode: '',
    // Coordinates for map
    latitude: null,
    longitude: null,
    photos: []
  });

  const categories = [
    { value: 'piattaforme_aeree', label: 'Piattaforme Aeree', icon: '🏗️' },
    { value: 'sollevamento', label: 'Sollevamento', icon: '🏋️' },
    { value: 'gru_edili', label: 'Gru Edili', icon: '🏗️' },
    { value: 'trabattelli', label: 'Trabattelli', icon: '🪜' },
    { value: 'autocarri_furgoni', label: 'Autocarri e Furgoni', icon: '🚚' },
    { value: 'movimento_terra', label: 'Movimento Terra', icon: '🚜' },
    { value: 'compattazione_vibrazione', label: 'Compattazione e Vibrazione', icon: '🔨' }
  ];

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setStep(2);
      setSelectedCategory('piattaforme_aeree');
      setEquipmentData({
        managerName: '',
        phoneNumber: '',
        rental_type: '',
        title: '',
        fuelType: '',
        maxHeight: '',
        nickname: '',
        setup: '',
        armType: '',
        movementMode: '',
        negativeWork: '',
        additionalFeatures: '',
        dailyRate: '',
        weeklyRate: '',
        monthlyRate: '',
        city: '',
        province: '',
        address: '',
        civicNumber: '',
        postalCode: '',
        latitude: null,
        longitude: null,
        photos: []
      });
    }
  }, [isOpen]);

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setStep(2);
  };

  const handleBack = () => {
    // Close the dialog instead of going back to category selection
    onOpenChange(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser || !currentUser.user_id) {
      alert('Errore: utente non autenticato');
      return;
    }
    
    try {
      const formData = new FormData();
      
      // Add owner_id
      formData.append('owner_id', currentUser.user_id);
      
      // Add all platform fields
      formData.append('title', equipmentData.title);
      formData.append('rental_type', equipmentData.rental_type || '');
      formData.append('fuel_type', equipmentData.fuelType);
      formData.append('max_height', equipmentData.maxHeight);
      
      if (equipmentData.setup) {
        formData.append('setup', equipmentData.setup);
      }
      if (equipmentData.armType) {
        formData.append('arm_type', equipmentData.armType);
      }
      if (equipmentData.movementMode) {
        formData.append('movement_mode', equipmentData.movementMode);
      }
      if (equipmentData.negativeWork) {
        formData.append('negative_work', equipmentData.negativeWork);
      }
      if (equipmentData.additionalFeatures) {
        formData.append('additional_features', equipmentData.additionalFeatures);
      }
      
      formData.append('daily_rate', equipmentData.dailyRate);
      formData.append('weekly_rate', equipmentData.weeklyRate);
      formData.append('monthly_rate', equipmentData.monthlyRate);
      
      // New detailed location fields
      formData.append('city', equipmentData.city);
      formData.append('province', equipmentData.province);
      formData.append('address', equipmentData.address);
      formData.append('civic_number', equipmentData.civicNumber);
      formData.append('postal_code', equipmentData.postalCode);
      // Coordinates from map picker
      if (equipmentData.latitude) formData.append('latitude', equipmentData.latitude.toString());
      if (equipmentData.longitude) formData.append('longitude', equipmentData.longitude.toString());

      // Note: no photo upload — image auto-assigned by platform type on the backend.

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/platforms`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la registrazione');
      }
      
      alert('Piattaforma registrata con successo!');
      onOpenChange(false);
      
      // Reload the page to show the new platform
      window.location.reload();
    } catch (error) {
      console.error('Errore:', error);
      alert('Errore durante la registrazione: ' + error.message);
    }
  };

  const updateField = (field, value) => {
    setEquipmentData({ ...equipmentData, [field]: value });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto">
        {step === 1 ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl">Registra la tua Attrezzatura</DialogTitle>
              <DialogDescription>
                Seleziona la categoria della tua attrezzatura per iniziare
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-6">
              {categories.map((category) => (
                <Card 
                  key={category.value}
                  className="cursor-pointer hover:shadow-lg hover:border-orange-500 transition-all duration-200"
                  onClick={() => handleCategorySelect(category.value)}
                >
                  <CardContent className="p-6 flex items-center space-x-4">
                    <span className="text-4xl">{category.icon}</span>
                    <div>
                      <h3 className="font-semibold text-lg">{category.label}</h3>
                      <p className="text-sm text-slate-500">Clicca per continuare</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center space-x-2">
                <Button variant="ghost" size="sm" onClick={handleBack}>
                  ← Indietro
                </Button>
              </div>
              <DialogTitle className="text-2xl">
                Registra una nuova piattaforma aerea
              </DialogTitle>
              <DialogDescription>
                Compila tutti i campi per registrare la tua piattaforma aerea
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6 py-4">
              <div className="space-y-4">
                {/* Ragione Sociale */}
                <div className="space-y-2">
                  <Label htmlFor="equipment_manager_name">
                    Ragione Sociale * <span className="text-xs text-slate-500 font-normal">(apparirà al cliente al momento della sua iscrizione/accesso a quandomiserve.it)</span>
                  </Label>
                  <Input
                    id="equipment_manager_name"
                    value={equipmentData.managerName}
                    onChange={(e) => updateField('managerName', e.target.value)}
                    placeholder="es. Mario Rossi"
                    required
                  />
                </div>

                {/* Numero di Contatto */}
                <div className="space-y-2">
                  <Label htmlFor="equipment_phone_number">
                    Numero di Contatto * <span className="text-xs text-slate-500 font-normal">(apparirà al cliente al momento della sua iscrizione/accesso a quandomiserve.it)</span>
                  </Label>
                  <Input
                    id="equipment_phone_number"
                    type="tel"
                    value={equipmentData.phoneNumber}
                    onChange={(e) => updateField('phoneNumber', e.target.value)}
                    placeholder="+39 123 456 7890"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="equipment_rental_type">Tipo di Noleggio *</Label>
                  <Select value={equipmentData.rental_type || ''} onValueChange={(value) => updateField('rental_type', value)} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona tipo di noleggio" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="freddo">Noleggio a Freddo</SelectItem>
                      <SelectItem value="caldo">Noleggio a Caldo</SelectItem>
                      <SelectItem value="freddo_caldo">Noleggio a Freddo e Caldo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="equipment_title">Titolo Annuncio *</Label>
                  <Select value={equipmentData.title} onValueChange={(value) => updateField('title', value)} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona tipo di piattaforma" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="autocarrata">Autocarrata</SelectItem>
                      <SelectItem value="sottoponte">Sottoponte</SelectItem>
                      <SelectItem value="ragno">Ragno</SelectItem>
                      <SelectItem value="semovente">Semovente</SelectItem>
                      <SelectItem value="elevatore_colonna">Elevatore a Colonna</SelectItem>
                      <SelectItem value="verticale">Verticale</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="equipment_fuel_type">Alimentazione *</Label>
                  <Select value={equipmentData.fuelType} onValueChange={(value) => updateField('fuelType', value)} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona alimentazione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="benzina">Benzina</SelectItem>
                      <SelectItem value="gasolio">Gasolio</SelectItem>
                      <SelectItem value="bifuel">Bifuel</SelectItem>
                      <SelectItem value="elettrica">Elettrica</SelectItem>
                      <SelectItem value="ibrida">Ibrida</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="equipment_max_height">Altezza di Lavoro Massima (metri) *</Label>
                  <Input
                    id="equipment_max_height"
                    type="number"
                    min="0"
                    value={equipmentData.maxHeight}
                    onChange={(e) => updateField('maxHeight', e.target.value)}
                    placeholder="es. 20"
                    required
                  />
                </div>

                {equipmentData.title === 'autocarrata' && (
                  <div className="space-y-2">
                    <Label htmlFor="equipment_setup">Allestimento *</Label>
                    <Select value={equipmentData.setup} onValueChange={(value) => updateField('setup', value)} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona allestimento" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="autocarro">Autocarro</SelectItem>
                        <SelectItem value="porter">Porter</SelectItem>
                        <SelectItem value="pickup">Pickup</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {(equipmentData.title === 'autocarrata' || 
                  equipmentData.title === 'ragno' || 
                  equipmentData.title === 'semovente') && (
                  <div className="space-y-2">
                    <Label htmlFor="equipment_arm_type">Tipo di Braccio *</Label>
                    <Select value={equipmentData.armType} onValueChange={(value) => updateField('armType', value)} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona tipo di braccio" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="articolato">Articolato</SelectItem>
                        <SelectItem value="telescopico">Telescopico</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="equipment_movement_mode">Modalità di Spostamento *</Label>
                  <Input
                    id="equipment_movement_mode"
                    value={equipmentData.movementMode}
                    onChange={(e) => updateField('movementMode', e.target.value)}
                    placeholder="es. Ruote, Cingoli, Stabilizzatori"
                    required
                  />
                </div>

                {(equipmentData.title === 'autocarrata' || 
                  equipmentData.title === 'sottoponte' || 
                  equipmentData.title === 'ragno' || 
                  equipmentData.title === 'semovente') && (
                  <div className="space-y-2">
                    <Label htmlFor="equipment_negative_work">Lavoro in Negativo *</Label>
                    <Select value={equipmentData.negativeWork} onValueChange={(value) => updateField('negativeWork', value)} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="si">Sì</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="equipment_additional_features">Caratteristiche Aggiuntive se Esistenti</Label>
                  <Textarea
                    id="equipment_additional_features"
                    value={equipmentData.additionalFeatures}
                    onChange={(e) => updateField('additionalFeatures', e.target.value)}
                    placeholder="Descrivi eventuali caratteristiche speciali..."
                    rows={3}
                  />
                </div>

                <div className="space-y-3">
                  <Label className="text-sm font-medium">Tariffa Noleggio *</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="equipment_daily_rate" className="text-sm">Tariffa Giornaliera (€) *</Label>
                      <Input
                        id="equipment_daily_rate"
                        type="number"
                        min="0"
                        step="0.01"
                        value={equipmentData.dailyRate}
                        onChange={(e) => updateField('dailyRate', e.target.value)}
                        placeholder="es. 150.00"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="equipment_weekly_rate" className="text-sm">Tariffa Settimanale (€) *</Label>
                      <Input
                        id="equipment_weekly_rate"
                        type="number"
                        min="0"
                        step="0.01"
                        value={equipmentData.weeklyRate}
                        onChange={(e) => updateField('weeklyRate', e.target.value)}
                        placeholder="es. 900.00"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="equipment_monthly_rate" className="text-sm">Tariffa Mensile (€) *</Label>
                      <Input
                        id="equipment_monthly_rate"
                        type="number"
                        min="0"
                        step="0.01"
                        value={equipmentData.monthlyRate}
                        onChange={(e) => updateField('monthlyRate', e.target.value)}
                        placeholder="es. 3000.00"
                        required
                      />
                    </div>
                  </div>
                  <p className="text-xs text-slate-600 mt-2 italic" data-testid="equipment-vat-note">
                    I prezzi vanno inseriti al netto di IVA (cioè IVA esclusa).
                  </p>
                </div>

                {/* Nuovi campi località dettagliati */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="equipment_city">Città *</Label>
                    <Input
                      id="equipment_city"
                      value={equipmentData.city}
                      onChange={(e) => updateField('city', e.target.value)}
                      placeholder="es. Milano"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="equipment_province">Provincia *</Label>
                    <Input
                      id="equipment_province"
                      value={equipmentData.province}
                      onChange={(e) => updateField('province', e.target.value)}
                      placeholder="es. MI"
                      maxLength={2}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="equipment_address">Indirizzo *</Label>
                  <Input
                    id="equipment_address"
                    value={equipmentData.address}
                    onChange={(e) => updateField('address', e.target.value)}
                    placeholder="es. Via Roma"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="equipment_civic_number">Numero Civico *</Label>
                    <Input
                      id="equipment_civic_number"
                      value={equipmentData.civicNumber}
                      onChange={(e) => updateField('civicNumber', e.target.value)}
                      placeholder="es. 123 o SNC"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="equipment_postal_code">CAP *</Label>
                    <Input
                      id="equipment_postal_code"
                      value={equipmentData.postalCode}
                      onChange={(e) => updateField('postalCode', e.target.value)}
                      placeholder="es. 20100"
                      maxLength={5}
                      required
                    />
                  </div>
                </div>

                {/* Mini Mappa per verifica posizione */}
                <LocationPickerMap
                  city={equipmentData.city}
                  province={equipmentData.province}
                  address={equipmentData.address}
                  civicNumber={equipmentData.civicNumber}
                  postalCode={equipmentData.postalCode}
                  latitude={equipmentData.latitude}
                  longitude={equipmentData.longitude}
                  onLocationChange={(lat, lng) => setEquipmentData({...equipmentData, latitude: lat, longitude: lng})}
                />

                <div className="space-y-2">
                  <Label>Foto Piattaforma</Label>
                  <p className="text-xs text-slate-600">
                    L'immagine dell'attrezzatura viene assegnata automaticamente da quandomiserve.it in base alla tipologia selezionata. L'immagine rappresenta in modo generico la categoria del mezzo: inserisci nell'annuncio tutte le caratteristiche effettive dell'attrezzatura disponibile.
                  </p>
                  {equipmentData.title ? (
                    <div className="border-2 border-orange-200 rounded-lg p-3 bg-orange-50 flex items-center gap-4">
                      <img
                        src={`${BACKEND_URL}/api/uploads/type_${equipmentData.title}.png`}
                        alt={`Foto ${equipmentData.title}`}
                        className="w-32 h-32 object-contain bg-white rounded-md border border-orange-100"
                        data-testid="equipment-type-preview"
                      />
                      <div className="text-sm">
                        <p className="font-semibold text-orange-700 capitalize">{equipmentData.title.replace('_', ' ')}</p>
                        <p className="text-xs text-slate-600 mt-1">Questa è l'immagine che apparirà sulla scheda della tua piattaforma. È neutra, senza loghi: i clienti dovranno contattarti tramite il pulsante "Contatta Ora".</p>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 bg-slate-50 text-center">
                      <p className="text-sm text-slate-500">Seleziona il <strong>Tipo di Piattaforma Aerea</strong> per visualizzare la foto associata.</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
                <p className="text-sm text-yellow-800">
                  ⚠️ <strong>Importante:</strong> Tutti i campi contrassegnati con * sono obbligatori da compilare. Solo dopo la nostra verifica e conferma dei dati forniti (entro 48h), la piattaforma aerea sarà caricata automaticamente su quandomiserve.it. Riceverete una email di conferma e un messaggio nella voce "Notifiche quandomiserve.it" nella vostra area personale, che la registrazione della piattaforma aerea è andata a buon fine.
                </p>
                <p className="text-sm text-yellow-800 mt-3">
                  Chiediamo inoltre di prestare la massima tempestività nell'aggiornare la disponibilità delle vostre Piattaforme Aeree registrate, bloccando le date qualora doveste avere già una prenotazione a carico nei stessi giorni o per vostra volontà, nella vostra area personale nella sezione "Gestione Noleggi". Questo consente che le date da voi bloccate per quella Piattaforma Aerea non compaiano al cliente che ha cercato la stessa nelle medesime date, non procurandogli perdite di tempo. <strong>Qualora dovessimo ricevere continue lamentele per la vostra mancata attivazione potremmo sospendere o eliminare il vostro account aziendale.</strong>
                </p>
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Annulla
                </Button>
                <Button 
                  type="submit"
                  className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700"
                >
                  Registra Piattaforma
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RegisterEquipmentDialog;
